"use strict";

/**
 * src/pages/analysis/analysisAuthenticatedFetch.js
 *
 * PHASE_B1B-2A — Analysis Authenticated Fetch helper (NOT wired into
 * any page or route yet).
 *
 * A small, dependency-injectable frontend helper that attaches a
 * Firebase ID token to a single REST request and classifies the
 * resulting error into a small, stable typed-error contract. This
 * module is created so a LATER phase can replace a page's:
 *
 *   fetch(url, init)
 *
 * with:
 *
 *   analysisAuthenticatedFetch(user, url, init)
 *
 * while keeping that page's existing `response.json()` handling
 * completely unchanged for the success path.
 *
 * This module is CommonJS (matches the existing CJS pattern used by
 * other src/pages/analysis/*.js modules, e.g.
 * analysisSelfRegistrationClient.js) so it can be require()'d
 * directly by a Node self-check without any build step, while still
 * being consumable from the webpack/CRA frontend bundle exactly like
 * those other CJS modules already are.
 *
 * Dependency injection: analysisAuthenticatedFetch(user, url, init,
 * { fetchFn }) accepts an optional injected fetchFn, which lets
 * analysisAuthenticatedFetchSelfcheck.js exercise every branch below
 * fully offline — no real Firebase call, no real network call, ever.
 * The default fetchFn resolves the browser-global `fetch` lazily, at
 * CALL time (never at require time, never cached on the module).
 *
 * This module deliberately does NOT:
 *   - import the Firebase Auth SDK, the Firebase Admin SDK, or the
 *     Firestore Client SDK, or hold a global/module-level Firebase
 *     user reference — the caller passes a Firebase `user` in on
 *     every call,
 *   - cache an ID token across calls — a fresh token is read from
 *     `user.getIdToken()` immediately before every request,
 *   - re-implement the Basic/Pro/Premium feature matrix from
 *     src/utils/analysisEntitlementPolicy.js — the backend remains
 *     the sole source of truth for whether a request is allowed; this
 *     helper only classifies the backend's own response,
 *   - call response.json() on a successful (2xx) response, mutate a
 *     successful response's payload, or trigger any navigation/logout
 *     side effect,
 *   - mutate the caller's `init` object or `init.headers`,
 *   - retry more than once per call, or force-refresh the ID token
 *     more than once per call,
 *   - log a token, a UID, an email, a request, or a response body via
 *     console.* (no console.* calls exist in this file at all),
 *   - start any interval/timeout/background timer.
 */

// =========================================================================
// Generic helpers (no side effects)
// =========================================================================

function isPlainObject(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch (_e) {
    return false;
  }
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

// =========================================================================
// Typed error contract
// =========================================================================

/** Canonical, frozen error-kind values. */
const ANALYSIS_FETCH_ERROR_KINDS = Object.freeze({
  unauthorized: "unauthorized",
  account_disabled: "account_disabled",
  entitlement: "entitlement",
  feature_forbidden: "feature_forbidden",
  http: "http",
  network: "network",
});

const VALID_ERROR_KINDS = new Set(Object.keys(ANALYSIS_FETCH_ERROR_KINDS).map((key) => ANALYSIS_FETCH_ERROR_KINDS[key]));

/** Stable, generic (never backend/Firebase-derived) fallback messages. */
const STABLE_MESSAGES = Object.freeze({
  firebase_auth_required: "Firebase authentication is required.",
  token_refresh_failed: "Could not refresh the session.",
  unauthorized: "Unauthorized.",
  account_disabled: "Account is disabled.",
  no_entitlement: "No active entitlement.",
  entitlement_malformed: "Entitlement is malformed.",
  analysis_access_blocked: "Analysis access is blocked.",
  trial_expired: "Trial has expired.",
  access_expired: "Access has expired.",
  entitlement: "Entitlement is not active.",
  feature_forbidden: "Feature is not available on the current plan.",
  http_error: "Request failed.",
  network_error: "Network request failed.",
});

/**
 * Public error shape: instances only ever carry `name`, `message`,
 * `kind`, `status`, `code` (plus the JS-engine-added `stack`). Never
 * carries a token, an Authorization header, a Firebase user, a UID,
 * an email, the request `init`, the response object, a raw response
 * body, or a server/Firebase stack trace.
 */
class AnalysisAuthenticatedFetchError extends Error {
  constructor({ kind, status, code, message } = {}) {
    super(isNonEmptyString(message) ? message : "Request failed");
    this.name = "AnalysisAuthenticatedFetchError";
    this.kind = VALID_ERROR_KINDS.has(kind) ? kind : ANALYSIS_FETCH_ERROR_KINDS.http;
    this.status = typeof status === "number" && Number.isFinite(status) ? status : 0;
    this.code = isNonEmptyString(code) ? code : "request_failed";
  }
}

/**
 * Safe type guard for AnalysisAuthenticatedFetchError. Deliberately
 * does NOT rely on `instanceof` alone (webpack/module duplication can
 * produce a structurally-identical class from a different module
 * instance) — it checks the exact public shape instead, so an
 * arbitrary object is not mistaken for a typed error just because it
 * happens to share one or two fields.
 *
 * @param {*} error
 * @returns {boolean}
 */
function isAnalysisAuthenticatedFetchError(error) {
  if (!error || typeof error !== "object") return false;
  if (!(error instanceof Error)) return false;
  if (error.name !== "AnalysisAuthenticatedFetchError") return false;
  if (typeof error.message !== "string") return false;
  if (!VALID_ERROR_KINDS.has(error.kind)) return false;
  if (typeof error.status !== "number" || !Number.isFinite(error.status)) return false;
  if (typeof error.code !== "string" || error.code.trim() === "") return false;
  return true;
}

function makeTypedError(kind, status, code, message) {
  return new AnalysisAuthenticatedFetchError({ kind, status, code, message });
}

// =========================================================================
// Response-body -> typed-error classification (Section 7 of the spec)
// =========================================================================

/** 403 codes that mean "entitlement is not currently active" (never treated as network errors). */
const ENTITLEMENT_CODES = new Set([
  "no_entitlement",
  "entitlement_malformed",
  "analysis_access_blocked",
  "trial_expired",
  "access_expired",
]);

function extractBackendCode(parsedBody) {
  return isPlainObject(parsedBody) && isNonEmptyString(parsedBody.error) ? parsedBody.error.trim() : null;
}

function extractBackendMessage(parsedBody) {
  return isPlainObject(parsedBody) && isNonEmptyString(parsedBody.message) ? parsedBody.message.trim() : null;
}

/**
 * Builds the final typed error for a non-2xx response, given its HTTP
 * status and its (already safely-parsed) JSON body. Only whitelisted
 * `error`/`message` fields are ever read from `parsedBody` — nothing
 * else from the backend body is copied onto the resulting error.
 *
 * @param {number} status
 * @param {*} parsedBody
 * @returns {AnalysisAuthenticatedFetchError}
 */
function buildTypedErrorFromResponse(status, parsedBody) {
  const backendCode = extractBackendCode(parsedBody);
  const backendMessage = extractBackendMessage(parsedBody);

  if (status === 401) {
    const code = backendCode || "unauthorized";
    return makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, code, backendMessage || STABLE_MESSAGES.unauthorized);
  }

  if (status === 403) {
    if (backendCode === "account_disabled") {
      return makeTypedError(
        ANALYSIS_FETCH_ERROR_KINDS.account_disabled,
        403,
        "account_disabled",
        backendMessage || STABLE_MESSAGES.account_disabled
      );
    }
    if (backendCode && ENTITLEMENT_CODES.has(backendCode)) {
      return makeTypedError(
        ANALYSIS_FETCH_ERROR_KINDS.entitlement,
        403,
        backendCode,
        backendMessage || STABLE_MESSAGES[backendCode] || STABLE_MESSAGES.entitlement
      );
    }
    if (backendCode === "feature_forbidden") {
      return makeTypedError(
        ANALYSIS_FETCH_ERROR_KINDS.feature_forbidden,
        403,
        "feature_forbidden",
        backendMessage || STABLE_MESSAGES.feature_forbidden
      );
    }
  }

  const safeStatus = typeof status === "number" && Number.isFinite(status) ? status : 0;
  return makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.http, safeStatus, backendCode || "http_error", backendMessage || STABLE_MESSAGES.http_error);
}

// =========================================================================
// Default dependencies (resolved lazily at call-time, never at require-time)
// =========================================================================

/**
 * Default fetchFn: resolves the browser-global `fetch` at CALL-time
 * (every invocation looks it up fresh), never at require-time, never
 * cached on this module.
 */
function defaultFetchFn(...args) {
  const fetchImpl = typeof fetch === "function" ? fetch : null;
  if (typeof fetchImpl !== "function") {
    throw new Error("global fetch is not available in this environment");
  }
  return fetchImpl(...args);
}

/**
 * Parses a fetch-like Response's JSON body safely — returns null on a
 * missing `.json`, an empty/malformed body, or a body that throws
 * while parsing. Only ever called for a non-2xx response (Section 8).
 *
 * @param {*} response - a fetch-like Response object (or fake)
 * @returns {Promise<*>}
 */
async function safeParseJson(response) {
  if (!response || typeof response.json !== "function") {
    return null;
  }
  try {
    return await response.json();
  } catch (_e) {
    return null;
  }
}

// =========================================================================
// Header merging (never mutates the caller's init/headers)
// =========================================================================

/**
 * Normalizes any of the three header shapes the fetch API accepts
 * (plain object, array of [key, value] entries, or a Headers
 * instance) into a fresh array of [key, value] entries. Never mutates
 * `headersInput`.
 *
 * @param {*} headersInput
 * @returns {Array<[string, string]>}
 */
function normalizeHeaderEntries(headersInput) {
  if (headersInput === undefined || headersInput === null) {
    return [];
  }
  if (Array.isArray(headersInput)) {
    return headersInput
      .filter((entry) => Array.isArray(entry) && entry.length === 2)
      .map((entry) => [String(entry[0]), String(entry[1])]);
  }
  if (typeof Headers === "function" && headersInput instanceof Headers) {
    const entries = [];
    headersInput.forEach((value, key) => {
      entries.push([key, value]);
    });
    return entries;
  }
  if (isPlainObject(headersInput)) {
    return Object.keys(headersInput).map((key) => [key, headersInput[key]]);
  }
  return [];
}

/**
 * Builds a brand-new plain headers object carrying every caller
 * header EXCEPT any caller-supplied Authorization (matched case-
 * insensitively, since HTTP header names are case-insensitive), plus
 * exactly one Authorization header set to `authorizationValue`. The
 * helper's own Authorization is always the final value — a caller-
 * supplied Authorization is never trusted.
 *
 * @param {*} callerHeaders
 * @param {string} authorizationValue
 * @returns {Object<string, string>}
 */
function buildRequestHeaders(callerHeaders, authorizationValue) {
  const entries = normalizeHeaderEntries(callerHeaders);
  const result = {};
  for (const [key, value] of entries) {
    if (String(key).toLowerCase() === "authorization") continue;
    result[key] = value;
  }
  result.Authorization = authorizationValue;
  return result;
}

/**
 * Builds a brand-new request init object for a single fetch call:
 * every own property of the caller's `init` is preserved verbatim
 * (method/body/signal/cache/credentials/anything else) EXCEPT
 * `headers`, which is rebuilt via buildRequestHeaders(). Never
 * mutates the caller's `init` or `init.headers`. Never adds a `body`
 * key that the caller did not already supply.
 *
 * @param {*} callerInit
 * @param {string} token
 * @returns {Object}
 */
function buildRequestInit(callerInit, token) {
  const safeInit = isPlainObject(callerInit) ? callerInit : {};
  const nextInit = {};
  for (const key of Object.keys(safeInit)) {
    if (key === "headers") continue;
    nextInit[key] = safeInit[key];
  }
  nextInit.headers = buildRequestHeaders(safeInit.headers, `Bearer ${token}`);
  return nextInit;
}

// =========================================================================
// Fetch execution (network/Abort classification — Sections 5 & 7)
// =========================================================================

/**
 * Runs exactly one fetchFn(url, requestInit) call. AbortError is
 * rethrown unchanged (never wrapped, never retried). Any other thrown
 * error, or a response missing the minimal fetch-like shape
 * (`.ok` boolean + `.status` number), is classified as a stable
 * `network` typed error — the raw error/message is never copied onto
 * the public error.
 *
 * @param {Function} fetchFn
 * @param {*} url
 * @param {Object} requestInit
 * @returns {Promise<*>} a fetch-like Response
 */
async function performFetch(fetchFn, url, requestInit) {
  let response;
  try {
    response = await fetchFn(url, requestInit);
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw e;
    }
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.network, 0, "network_error", STABLE_MESSAGES.network_error);
  }

  if (!response || typeof response !== "object" || typeof response.ok !== "boolean" || typeof response.status !== "number") {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.network, 0, "network_error", STABLE_MESSAGES.network_error);
  }

  return response;
}

// =========================================================================
// Token acquisition helpers (Sections 4 & 5)
// =========================================================================

function assertValidUser(user) {
  if (!user || typeof user !== "object" || typeof user.getIdToken !== "function") {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "firebase_auth_required", STABLE_MESSAGES.firebase_auth_required);
  }
}

/**
 * Reads the user's current ID token (no force-refresh). Throws the
 * stable `firebase_auth_required` typed error if `getIdToken()`
 * rejects or resolves to anything other than a non-empty string.
 *
 * @param {*} user
 * @returns {Promise<string>}
 */
async function getInitialToken(user) {
  let token;
  try {
    token = await user.getIdToken();
  } catch (_e) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "firebase_auth_required", STABLE_MESSAGES.firebase_auth_required);
  }
  if (!isNonEmptyString(token)) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "firebase_auth_required", STABLE_MESSAGES.firebase_auth_required);
  }
  return token;
}

/**
 * Force-refreshes the user's ID token via `getIdToken(true)`. Throws
 * the stable `token_refresh_failed` typed error if the call rejects
 * or resolves to anything other than a non-empty string.
 *
 * @param {*} user
 * @returns {Promise<string>}
 */
async function forceRefreshToken(user) {
  let token;
  try {
    token = await user.getIdToken(true);
  } catch (_e) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "token_refresh_failed", STABLE_MESSAGES.token_refresh_failed);
  }
  if (!isNonEmptyString(token)) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "token_refresh_failed", STABLE_MESSAGES.token_refresh_failed);
  }
  return token;
}

/**
 * Force-refreshes the user's ID token *claims* via
 * `getIdTokenResult(true)` and returns the fresh `.token` string.
 * Callers must only invoke this after confirming
 * `typeof user.getIdTokenResult === "function"` (see the call site in
 * analysisAuthenticatedFetch below). Throws the stable
 * `token_refresh_failed` typed error if the call rejects or does not
 * resolve to an object with a non-empty-string `.token`.
 *
 * @param {*} user
 * @returns {Promise<string>}
 */
async function forceRefreshTokenViaClaims(user) {
  let tokenResult;
  try {
    tokenResult = await user.getIdTokenResult(true);
  } catch (_e) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "token_refresh_failed", STABLE_MESSAGES.token_refresh_failed);
  }
  const token = isPlainObject(tokenResult) ? tokenResult.token : undefined;
  if (!isNonEmptyString(token)) {
    throw makeTypedError(ANALYSIS_FETCH_ERROR_KINDS.unauthorized, 401, "token_refresh_failed", STABLE_MESSAGES.token_refresh_failed);
  }
  return token;
}

// =========================================================================
// analysisAuthenticatedFetch — main entry point (Sections 3, 4, 5)
// =========================================================================

/**
 * Sends a single logical authenticated request, attaching the given
 * Firebase user's current ID token as `Authorization: Bearer <token>`
 * and, ONLY on a first 401 or a first 403 `feature_forbidden`
 * response, retrying exactly once with a freshly-refreshed token.
 *
 * At most 2 network requests and at most 1 force-refresh happen per
 * call — see Section 5 of the PHASE_B1B-2A spec.
 *
 * On a final 2xx response, the raw fetch-like Response object is
 * returned as-is (never parsed, never mutated) so the caller's own
 * existing `response.json()` handling keeps working unchanged. On any
 * other final outcome, this throws an AnalysisAuthenticatedFetchError
 * (or, for an AbortError from `fetchFn`, rethrows that exact
 * AbortError unchanged).
 *
 * @param {{getIdToken: Function, getIdTokenResult?: Function}} user - a Firebase Auth user (or fake)
 * @param {string} url
 * @param {Object} [init] - a fetch-like RequestInit; never mutated
 * @param {{fetchFn?: Function}} [options] - optional injected fetchFn for offline self-check
 * @returns {Promise<*>} a fetch-like Response (only ever the final successful one)
 */
async function analysisAuthenticatedFetch(user, url, init, options) {
  const opts = isPlainObject(options) ? options : {};
  const fetchFn = typeof opts.fetchFn === "function" ? opts.fetchFn : defaultFetchFn;

  assertValidUser(user);
  const initialToken = await getInitialToken(user);

  const firstResponse = await performFetch(fetchFn, url, buildRequestInit(init, initialToken));
  if (firstResponse.ok) {
    return firstResponse;
  }

  if (firstResponse.status === 401) {
    const refreshedToken = await forceRefreshToken(user);
    const retryResponse = await performFetch(fetchFn, url, buildRequestInit(init, refreshedToken));
    if (retryResponse.ok) {
      return retryResponse;
    }
    // Already force-refreshed once for this call — classify the final
    // outcome directly, even if it is itself a 403 feature_forbidden.
    const parsedBody = await safeParseJson(retryResponse);
    throw buildTypedErrorFromResponse(retryResponse.status, parsedBody);
  }

  if (firstResponse.status === 403) {
    const parsedBody = await safeParseJson(firstResponse);
    const backendCode = extractBackendCode(parsedBody);

    if (backendCode === "feature_forbidden") {
      if (typeof user.getIdTokenResult !== "function") {
        // No claims-refresh capability available on this user — the
        // refresh/retry step from Section 5 is a no-op, so classify
        // the original response directly as feature_forbidden.
        throw buildTypedErrorFromResponse(firstResponse.status, parsedBody);
      }
      const refreshedToken = await forceRefreshTokenViaClaims(user);
      const retryResponse = await performFetch(fetchFn, url, buildRequestInit(init, refreshedToken));
      if (retryResponse.ok) {
        return retryResponse;
      }
      const retryParsedBody = await safeParseJson(retryResponse);
      throw buildTypedErrorFromResponse(retryResponse.status, retryParsedBody);
    }

    throw buildTypedErrorFromResponse(firstResponse.status, parsedBody);
  }

  const parsedBody = await safeParseJson(firstResponse);
  throw buildTypedErrorFromResponse(firstResponse.status, parsedBody);
}

module.exports = {
  ANALYSIS_FETCH_ERROR_KINDS,
  AnalysisAuthenticatedFetchError,
  analysisAuthenticatedFetch,
  isAnalysisAuthenticatedFetchError,
};
